import React, { useEffect, useState } from 'react';
import { TWSelect } from '../../../components/ui';
import companyApi from '../../../services/companyApi';
import storeApi from '../../../services/storeApi';

const toOptions = (rows) => (rows || []).map((r) => ({ value: r.id, label: r.name }));

// Cascading company -> store pair. A company change clears the store value
// (the old store belongs to another company). storeMode 'multiple' keeps an
// array of store ids, 'single' one id; '' means nothing chosen. `placeholders`
// overrides the company (default 'Any company') and store (default 'Any store')
// placeholders for callers where an empty pick means no target. The optional
// onStoreName(name) reports the label of the picked store ('' when none), for
// callers that need a name and not only the id.
const CompanyStoreSelect = ({
  companyId,
  storeId,
  onChange,
  onStoreName,
  storeMode = 'single',
  labels = {},
  placeholders = {},
  className = '',
}) => {
  const multiple = storeMode === 'multiple';
  const [companies, setCompanies] = useState([]);
  const [stores, setStores] = useState([]);

  useEffect(() => {
    let cancelled = false;
    // Company/Lookup returns every non-deleted company by name; list() without
    // a query is the API's default page (10 newest) and would hide older ones.
    companyApi
      .lookup()
      .then((res) => {
        if (!cancelled) setCompanies(toOptions(res?.data?.data));
      })
      .catch(() => {
        if (!cancelled) setCompanies([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!companyId) {
      setStores([]);
      return undefined;
    }
    let cancelled = false;
    storeApi
      .list('companyid=' + companyId)
      .then((res) => {
        if (!cancelled) setStores(toOptions(res?.data?.data));
      })
      .catch(() => {
        if (!cancelled) setStores([]);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId]);

  const emptyStore = multiple ? [] : '';

  return (
    <div className={`grid grid-cols-1 gap-3 sm:grid-cols-2 ${className}`}>
      <TWSelect
        label={labels.company || 'Company'}
        placeholder={placeholders.company || 'Any company'}
        options={companies}
        value={companyId || undefined}
        onChange={(value) => {
          onStoreName?.('');
          onChange({ companyid: value || '', storeid: emptyStore });
        }}
      />
      <TWSelect
        label={labels.store || 'Store'}
        placeholder={companyId ? placeholders.store || 'Any store' : 'Choose a company first'}
        options={stores}
        disabled={!companyId}
        mode={multiple ? 'multiple' : undefined}
        value={multiple ? storeId || [] : storeId || undefined}
        onChange={(value, option) => {
          if (!multiple) onStoreName?.(value ? String(option?.label ?? '') : '');
          onChange({ companyid: companyId, storeid: multiple ? value || [] : value || '' });
        }}
      />
    </div>
  );
};

export default CompanyStoreSelect;
